/**
 * "Choose from templates" — the library picker.
 *
 * Templates are standalone objects, so this lists every saved one regardless of
 * which campaign (if any) created it. Picking one binds it to the campaign in
 * hand; the same template can back as many campaigns as you like.
 */
import React from 'react'
import { Button, Dialog, Input } from '@/components/ds'
import { Ic } from '@/components/icons'
import { Spinner } from '@/components/common'
import { relativeTime } from '@/lib/format'
import { renderEmail } from '@shared/render'
import type { Lead, Template } from '@shared/types'
import { SAMPLE_LEAD } from './TemplateViewer'

export function TemplatePickerDialog({
  templates,
  loading,
  boundTemplateId,
  sampleLead,
  busy,
  onClose,
  onChoose,
}: {
  templates: Template[]
  loading?: boolean
  /** the one already bound, marked so re-picking it is obviously a no-op */
  boundTemplateId?: string | null
  sampleLead?: Pick<Lead, 'name' | 'email' | 'fields'>
  busy?: boolean
  onClose: () => void
  onChoose: (t: Template) => void
}) {
  const [search, setSearch] = React.useState('')
  const [selectedId, setSelectedId] = React.useState<string | null>(boundTemplateId ?? null)

  const lead = sampleLead ?? SAMPLE_LEAD
  const term = search.trim().toLowerCase()
  const rows = term
    ? templates.filter((t) =>
        `${t.name ?? ''} ${t.subject}`.toLowerCase().includes(term),
      )
    : templates

  const selected = rows.find((t) => t.id === selectedId) ?? rows[0] ?? null

  return (
    <Dialog
      open
      onClose={onClose}
      width={860}
      title="Choose from templates"
      footer={
        <>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', marginRight: 'auto' }}>
            {templates.length} saved template{templates.length === 1 ? '' : 's'} in your library
          </span>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!selected || busy}
            icon={<Ic.check size={14} />}
            onClick={() => selected && onChoose(selected)}
          >
            {busy ? 'Binding…' : 'Use this template'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Input
          prefix={<Ic.search size={14} />}
          placeholder="Search by name or subject…"
          value={search}
          onChange={setSearch}
        />

        {loading ? (
          <div
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              padding: '20px 0',
              color: 'var(--text-muted)',
              fontSize: 13,
            }}
          >
            <Spinner size={14} />
            Loading your library…
          </div>
        ) : rows.length === 0 ? (
          <div style={{ padding: '18px 2px', fontSize: 13, color: 'var(--text-muted)' }}>
            {templates.length === 0
              ? 'No saved templates yet — write one or paste HTML, and it joins your library.'
              : `Nothing matches “${search}”.`}
          </div>
        ) : (
          <div className="picker-split" style={{ display: 'flex', gap: 12, minHeight: 0 }}>
            {/* list */}
            <div
              className="picker-list"
              style={{
                width: 300,
                flex: 'none',
                maxHeight: 380,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {rows.map((t) => (
                <PickerRow
                  key={t.id}
                  template={t}
                  active={selected?.id === t.id}
                  bound={t.id === boundTemplateId}
                  onClick={() => setSelectedId(t.id)}
                />
              ))}
            </div>

            {/* small preview of the highlighted one */}
            <div
              style={{
                flex: 1,
                minWidth: 0,
                border: '1px solid var(--border-default)',
                borderRadius: 8,
                overflow: 'hidden',
                background: 'var(--surface-preview)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {selected && <MiniPreview template={selected} lead={lead} />}
            </div>
          </div>
        )}
      </div>
    </Dialog>
  )
}

function PickerRow({
  template,
  active,
  bound,
  onClick,
}: {
  template: Template
  active: boolean
  bound: boolean
  onClick: () => void
}) {
  const [hover, setHover] = React.useState(false)
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        border: `1px solid ${active ? 'var(--accent-soft-border)' : 'transparent'}`,
        borderRadius: 6,
        padding: '8px 10px',
        cursor: 'pointer',
        fontFamily: 'var(--font-sans)',
        background: active ? 'var(--accent-soft)' : hover ? 'var(--surface-sunken)' : 'transparent',
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
        <span
          style={{
            flex: 1,
            fontSize: 13.5,
            fontWeight: 600,
            color: 'var(--text-primary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {template.name?.trim() || template.subject || '(untitled)'}
        </span>
        {bound && (
          <span
            style={{
              flex: 'none',
              fontSize: 9.5,
              fontWeight: 700,
              letterSpacing: '.04em',
              padding: '1px 6px',
              borderRadius: 999,
              background: 'var(--accent-soft)',
              color: 'var(--accent-text)',
            }}
          >
            IN USE
          </span>
        )}
      </span>
      <span
        style={{
          display: 'block',
          fontSize: 12,
          color: 'var(--text-muted)',
          marginTop: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {template.subject}
      </span>
      <span style={{ display: 'block', fontSize: 11, color: 'var(--text-faint)', marginTop: 2 }}>
        {template.body_mode === 'html' ? 'Pasted HTML' : 'Rich text'} ·{' '}
        {relativeTime(template.created_at)}
      </span>
    </button>
  )
}

/** Merged subject + the rendered body, small. Same renderer as everywhere else. */
function MiniPreview({
  template,
  lead,
}: {
  template: Template
  lead: Pick<Lead, 'name' | 'email' | 'fields'>
}) {
  const rendered = React.useMemo(
    () => renderEmail(template, lead, 'https://example.com/unsubscribe?t=preview'),
    [template, lead],
  )

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '9px 12px',
          background: 'linear-gradient(90deg,#eceafb,#f3f0fb)',
          borderBottom: '1px solid var(--border-default)',
          flex: 'none',
        }}
      >
        <span
          style={{
            fontSize: 9.5,
            fontWeight: 700,
            color: 'var(--accent-text)',
            background: '#dedcfb',
            borderRadius: 4,
            padding: '2px 6px',
            letterSpacing: '.04em',
            flex: 'none',
          }}
        >
          SUBJECT
        </span>
        <span
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: '#1c1f2e',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {rendered.subject || '(no subject)'}
        </span>
      </div>
      <iframe
        title="Template preview"
        // sandbox="" — inert markup, no scripts, no navigation.
        sandbox=""
        srcDoc={rendered.html}
        style={{ display: 'block', width: '100%', height: 330, border: 'none', background: '#fff' }}
      />
    </>
  )
}
